import { useId } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import type { DailyRouteStop } from '@apis/reports';
import { APP_DEFAULT_TZ, formatTimeRange, formatDuration } from '@utils/dateUtils';
import { CardIcon } from '@components/Pages/AnalyticsPage/CardIcon';
import styles from './StopsRail.module.css';

/**
 * Subset of `DailyRouteStop` actually rendered by the rail. The component
 * only reads what it shows; the parent passes the full record from the BFF.
 */
type StopForRail = Pick<
  DailyRouteStop,
  | 'stopIndex'
  | 'floorNick'
  | 'areaNick'
  | 'arrivalIso'
  | 'departureIso'
  | 'durationMinutes'
  | 'personnel'
>;

interface IStopsRailProps {
  stops: StopForRail[];
  selectedIndex: number;
  onSelect: (stopIndex: number) => void;
  subjectName: string;
  /**
   * i18n key namespace used for the rail heading (`${i18nNamespace}.routeOf`)
   * and the personnel chip's accessible label
   * (`${i18nNamespace}.section.personnel`). Lets the same rail serve
   * different report surfaces (Daily Route, Incident Report) without forking.
   */
  i18nNamespace: string;
  /**
   * Optional stop index that should render a danger-ring marker on its
   * bullet, on top of the normal selected/unselected state. The Incident
   * Report uses this to highlight the at-incident stop ("the subject was
   * here at the incident instant") even while the user navigates away from
   * it. Pass `null` (or omit) when no marker is wanted.
   */
  markedIndex?: number | null;
}

/**
 * Vertical numbered "stops" rail. Each card represents one stop on the
 * subject's path, carrying floor + area, time range, duration, and a
 * co-presence count.
 *
 * Implements ARIA listbox semantics so the whole rail behaves as a single
 * keyboard-navigable list. Arrow keys move selection (clamped to bounds)
 * relative to the *currently selected* index — not the focused option —
 * matching native listbox behavior. Home/End jump to the first/last stop.
 * Enter/Space activates the focused option via the native button click.
 *
 * Shared between the Daily Route and Incident Report surfaces; the
 * incident variant supplies `markedIndex` to add a danger ring to the
 * at-incident bullet.
 */
export function StopsRail({
  stops,
  selectedIndex,
  onSelect,
  subjectName,
  i18nNamespace,
  markedIndex = null,
}: IStopsRailProps) {
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const headingId = useId();
  const heading = t(`${i18nNamespace}.routeOf`, { name: subjectName });
  const personnelLabel = t(`${i18nNamespace}.section.personnel`);

  // Derive bounds from the actual stopIndex range — robust if the BFF ever
  // returns a non-1-based or non-contiguous range.
  const firstIdx = stops[0]?.stopIndex;
  const lastIdx = stops[stops.length - 1]?.stopIndex;

  const handleListboxKey = (keyEvent: KeyboardEvent<HTMLDivElement>) => {
    if (firstIdx == null || lastIdx == null) return;
    if (keyEvent.key === 'ArrowDown') {
      keyEvent.preventDefault();
      onSelect(Math.min(lastIdx, selectedIndex + 1));
    } else if (keyEvent.key === 'ArrowUp') {
      keyEvent.preventDefault();
      onSelect(Math.max(firstIdx, selectedIndex - 1));
    } else if (keyEvent.key === 'Home') {
      keyEvent.preventDefault();
      onSelect(firstIdx);
    } else if (keyEvent.key === 'End') {
      keyEvent.preventDefault();
      onSelect(lastIdx);
    }
  };

  return (
    <section className={styles.rail} aria-labelledby={headingId}>
      {/* The frame is the white surface that wraps the heading AND the
          listbox together — the heading sits inside the same card chrome
          as the rows so they read as a single unit. */}
      <div className={styles.frame}>
        <h2 id={headingId} className={styles.heading}>
          {heading}
        </h2>
        <div
          role="listbox"
          aria-labelledby={headingId}
          className={styles.list}
          // tabIndex={-1} so the listbox can be a keydown event target — each
          // option button still manages its own focus; arrow keys bubble up
          // from the focused button to this listbox-level handler.
          tabIndex={-1}
          onKeyDown={handleListboxKey}
        >
          {stops.map((stop) => {
            const selected = stop.stopIndex === selectedIndex;
            const marked = stop.stopIndex === markedIndex;
            const cardClass = [
              styles.card,
              selected ? styles.selected : '',
              marked ? styles.marked : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={stop.stopIndex}
                type="button"
                role="option"
                aria-selected={selected}
                className={cardClass}
                onClick={() => onSelect(stop.stopIndex)}
              >
                {/* Bullet sits in its own gutter to the left of the card body
                    (mirrors trusstor's TimelineItem). The connecting line in
                    `.list::before` threads through the bullet centres. */}
                <span className={styles.bullet} aria-hidden="true">
                  {stop.stopIndex}
                </span>
                <span className={styles.cardBody}>
                  {/* Two independent flex rows so the floor name (top) never
                      dictates the column widths of the area/time/duration row
                      (bottom) — that coupling used to reserve floor-width space
                      under the short area code and push the duration out of the
                      frame. */}
                  <span className={styles.topRow}>
                    <span className={styles.floor}>{stop.floorNick}</span>
                    <span
                      className={styles.coPresence}
                      aria-label={personnelLabel}
                    >
                      <CardIcon name="helmet" size={14} />
                      {stop.personnel.length}
                    </span>
                  </span>
                  <span className={styles.bottomRow}>
                    <span className={styles.area}>{stop.areaNick}</span>
                    <span className={styles.times}>
                      {formatTimeRange(stop.arrivalIso, stop.departureIso, tz)}
                    </span>
                    <span className={styles.duration}>
                      {formatDuration(stop.durationMinutes)}
                    </span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
